import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  GraduationCap, CalendarDays, ClipboardList, Target, BookMarked,
  PencilRuler, TrendingUp, Sparkles, Check, ArrowRight, Menu, X,
} from "lucide-react";
import { useState } from "react";
import { PLANS } from "@/lib/constants";

const FEATURES = [
  { icon: CalendarDays, title: "Emploi du temps", desc: "Tes cours, tes salles, tes profs : tout ton emploi du temps toujours sous la main.", color: "bg-indigo-100 text-indigo-600" },
  { icon: ClipboardList, title: "Devoirs", desc: "Note tes devoirs, leurs dates limites et reçois des rappels pour ne plus rien oublier.", color: "bg-rose-100 text-rose-600" },
  { icon: Target, title: "Objectifs", desc: "Fixe-toi des objectifs du mois et suis ta progression avec des barres motivantes.", color: "bg-amber-100 text-amber-600" },
  { icon: BookMarked, title: "Fiches de révision", desc: "Crée et classe tes fiches par matière et par chapitre. Révise où tu veux.", color: "bg-emerald-100 text-emerald-600" },
  { icon: PencilRuler, title: "Exercices corrigés", desc: "Entraîne-toi avec des exercices classés par niveau et comprends tes erreurs.", color: "bg-sky-100 text-sky-600" },
  { icon: Sparkles, title: "Coach IA (Premium)", desc: "Génère des fiches et un planning de révision personnalisé grâce à l'IA.", color: "bg-violet-100 text-violet-600" },
];

export default function Landing() {
  const { user } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <div className="min-h-screen bg-[#FAFAFC]" data-testid="landing-page">
      {/* Nav */}
      <header className="sticky top-0 z-50 backdrop-blur-md bg-white/80 border-b border-slate-200/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5" data-testid="landing-logo">
            <div className="h-9 w-9 rounded-xl bg-gradient-to-br from-indigo-600 to-violet-500 flex items-center justify-center">
              <GraduationCap className="h-5 w-5 text-white" />
            </div>
            <span className="font-display font-bold text-lg tracking-tight text-slate-900">ProEdu</span>
          </div>
          <nav className="hidden md:flex items-center gap-8 text-sm font-medium text-slate-600">
            <a href="#features" data-testid="landing-nav-features" className="hover:text-indigo-600 transition-colors duration-200">Fonctionnalités</a>
            <a href="#pricing" data-testid="landing-nav-pricing" className="hover:text-indigo-600 transition-colors duration-200">Tarifs</a>
          </nav>
          <div className="hidden md:flex items-center gap-3">
            {user ? (
              <Link to="/dashboard" data-testid="nav-dashboard-cta" className="px-5 py-2.5 rounded-full bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 transition-colors duration-200">
                Mon tableau de bord
              </Link>
            ) : (
              <>
                <Link to="/auth" data-testid="nav-login-button" className="px-5 py-2.5 rounded-full text-sm font-semibold text-slate-700 hover:bg-slate-100 transition-colors duration-200">
                  Se connecter
                </Link>
                <Link to="/auth?mode=register" data-testid="nav-register-button" className="px-5 py-2.5 rounded-full bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 transition-colors duration-200">
                  Créer un compte
                </Link>
              </>
            )}
          </div>
          <button className="md:hidden p-2" onClick={() => setMenuOpen(!menuOpen)} data-testid="mobile-menu-toggle">
            {menuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
        {menuOpen && (
          <div className="md:hidden border-t border-slate-100 bg-white px-4 py-4 space-y-2" data-testid="mobile-menu">
            <a href="#features" onClick={() => setMenuOpen(false)} className="block py-2 text-sm font-medium text-slate-700">Fonctionnalités</a>
            <a href="#pricing" onClick={() => setMenuOpen(false)} className="block py-2 text-sm font-medium text-slate-700">Tarifs</a>
            <Link to="/auth" data-testid="mobile-nav-login" className="block py-2 text-sm font-medium text-slate-700">Se connecter</Link>
            <Link to="/auth?mode=register" data-testid="mobile-nav-register" className="block py-2 text-sm font-semibold text-indigo-600">Créer un compte</Link>
          </div>
        )}
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-50 via-white to-violet-50" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-20 sm:pt-24 sm:pb-28">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="animate-fade-in-up">
              <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-indigo-100 text-indigo-700 text-xs font-bold uppercase tracking-wider" data-testid="hero-badge">
                <Sparkles className="h-3.5 w-3.5" />
                Pour collégiens & lycéens
              </span>
              <h1 className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-tight text-slate-900">
                Toute ta réussite scolaire,{" "}
                <span className="bg-gradient-to-r from-indigo-600 via-purple-600 to-violet-500 bg-clip-text text-transparent">
                  au même endroit.
                </span>
              </h1>
              <p className="mt-6 text-base sm:text-lg text-slate-600 leading-relaxed max-w-xl">
                Emploi du temps, devoirs, objectifs, fiches de révision et exercices corrigés :
                ProEdu réunit tous les outils dont tu as besoin pour t'organiser, travailler
                efficacement et progresser toute l'année.
              </p>
              <div className="mt-8 flex flex-wrap gap-4">
                <Link
                  to={user ? "/dashboard" : "/auth?mode=register"}
                  data-testid="hero-get-started-button"
                  className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-indigo-600 text-white font-semibold hover:bg-indigo-700 transition-colors duration-200 shadow-lg shadow-indigo-600/25"
                >
                  Commencer gratuitement
                  <ArrowRight className="h-4 w-4" />
                </Link>
                <a
                  href="#pricing"
                  data-testid="hero-pricing-button"
                  className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-white text-slate-800 font-semibold border border-slate-200 hover:border-indigo-300 hover:text-indigo-700 transition-colors duration-200"
                >
                  Voir les tarifs
                </a>
              </div>
              <div className="mt-10 flex items-center gap-6 text-sm text-slate-500">
                <div><span className="font-bold text-slate-900 text-lg">6</span> outils réunis</div>
                <div className="h-8 w-px bg-slate-200" />
                <div><span className="font-bold text-slate-900 text-lg">100 %</span> gratuit pour démarrer</div>
                <div className="h-8 w-px bg-slate-200" />
                <div><span className="font-bold text-slate-900 text-lg">6e → Tale</span> tous niveaux</div>
              </div>
            </div>
            <div className="relative hidden lg:block animate-fade-in">
              <div className="grid grid-cols-2 gap-4">
                <img
                  src="https://images.pexels.com/photos/6958511/pexels-photo-6958511.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                  alt="Élève qui révise"
                  className="rounded-3xl object-cover h-64 w-full shadow-xl"
                  data-testid="hero-image-1"
                />
                <img
                  src="https://images.pexels.com/photos/6345340/pexels-photo-6345340.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                  alt="Étudiante organisée"
                  className="rounded-3xl object-cover h-64 w-full shadow-xl mt-10"
                  data-testid="hero-image-2"
                />
              </div>
              <div className="absolute -bottom-6 left-8 bg-white rounded-2xl shadow-xl border border-slate-100 px-5 py-4 flex items-center gap-3" data-testid="hero-floating-card">
                <div className="h-10 w-10 rounded-xl bg-emerald-100 flex items-center justify-center">
                  <TrendingUp className="h-5 w-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-sm font-bold text-slate-900">+2 points de moyenne</p>
                  <p className="text-xs text-slate-500">avec 25 min de révision / jour</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="py-20 sm:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-slate-900">
              Tout ce qu'il te faut pour cartoner cette année
            </h2>
            <p className="mt-4 text-slate-600 text-base sm:text-lg">
              Fini les devoirs oubliés et les révisions de dernière minute. ProEdu t'aide à garder le cap, jour après jour.
            </p>
          </div>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {FEATURES.map(({ icon: Icon, title, desc, color }) => (
              <div
                key={title}
                data-testid={`feature-card-${title.toLowerCase().replace(/[^a-z]/g, "-")}`}
                className="group bg-white rounded-3xl border border-slate-200/80 p-6 shadow-sm hover:shadow-md hover:-translate-y-1 transition-transform duration-200"
              >
                <div className={`h-12 w-12 rounded-2xl ${color} flex items-center justify-center`}>
                  <Icon className="h-6 w-6" />
                </div>
                <h3 className="mt-5 text-lg sm:text-xl font-semibold text-slate-900">{title}</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-20 sm:py-28 bg-gradient-to-b from-slate-50 to-indigo-50/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-slate-900">
              Une formule pour chaque élève
            </h2>
            <p className="mt-4 text-slate-600 text-base sm:text-lg">
              Commence gratuitement, passe à la vitesse supérieure quand tu veux.
            </p>
          </div>
          <div className="mt-12 grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {Object.entries(PLANS).map(([key, plan]) => (
              <div
                key={key}
                data-testid={`landing-plan-${key}`}
                className={`relative bg-white rounded-3xl border p-7 shadow-sm ${
                  key === "premium" ? "border-indigo-300 ring-2 ring-indigo-500/20" : "border-slate-200/80"
                }`}
              >
                {key === "premium" && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-gradient-to-r from-indigo-600 to-violet-600 text-white text-xs font-bold uppercase tracking-wider">
                    Populaire
                  </span>
                )}
                <h3 className="text-xl font-semibold text-slate-900">{plan.name}</h3>
                <p className="mt-1 text-sm text-slate-500">{plan.tagline}</p>
                <p className="mt-4">
                  <span className="text-4xl font-bold text-slate-900">{plan.price}</span>
                  <span className="text-sm text-slate-500 ml-1">{plan.period}</span>
                </p>
                {plan.priceYear && (
                  <p className="mt-1 text-xs font-semibold text-emerald-600">ou {plan.priceYear}/an — {plan.discount}</p>
                )}
                <ul className="mt-6 space-y-2.5">
                  {plan.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-slate-700">
                      <Check className="h-4 w-4 text-emerald-500 mt-0.5 shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>
                <Link
                  to={user ? "/pricing" : "/auth?mode=register"}
                  data-testid={`landing-plan-${key}-cta`}
                  className={`mt-7 block text-center px-5 py-3 rounded-full font-semibold text-sm transition-colors duration-200 ${
                    key === "premium"
                      ? "bg-gradient-to-r from-indigo-600 to-violet-600 text-white hover:opacity-90"
                      : "bg-slate-100 text-slate-800 hover:bg-indigo-100 hover:text-indigo-700"
                  }`}
                >
                  {key === "gratuit" ? "Commencer" : "Choisir cette formule"}
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="rounded-3xl bg-gradient-to-r from-indigo-600 via-purple-600 to-violet-500 px-8 py-14 shadow-2xl shadow-indigo-600/20">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-white">
              Prêt à transformer ta façon de réviser ?
            </h2>
            <p className="mt-4 text-indigo-100 text-base sm:text-lg">
              Crée ton compte en 30 secondes et organise ta première semaine.
            </p>
            <Link
              to={user ? "/dashboard" : "/auth?mode=register"}
              data-testid="cta-bottom-button"
              className="mt-8 inline-flex items-center gap-2 px-8 py-4 rounded-full bg-white text-indigo-700 font-bold hover:bg-indigo-50 transition-colors duration-200"
            >
              Créer mon compte gratuit
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      <footer className="border-t border-slate-200/70 bg-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="h-7 w-7 rounded-lg bg-gradient-to-br from-indigo-600 to-violet-500 flex items-center justify-center">
              <GraduationCap className="h-4 w-4 text-white" />
            </div>
            <span className="font-display font-bold text-slate-900">ProEdu</span>
          </div>
          <p className="text-xs text-slate-500">© 2026 ProEdu — La boîte à outils des élèves motivés.</p>
        </div>
      </footer>
    </div>
  );
}
